import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import {
  Box,
  Button,
  useToast,
  VStack,
  HStack,
  IconButton,
  Image,
  Flex,
  Table,
  Tbody,
  Tr,
  Td,
  Th,
  Thead,
  Spinner,
  Text,
} from '@chakra-ui/react';
import DeleteBlueIcon from '../../assets/images/delete-blue-icon.png';
import AddIcon from '../../assets/images/add.png';
import ModalComponent from '../Modal';
import Label from '../Label';
import AlertModalOverlay from '../AlertModalComponent';
import FinancialRuleForm from './FinancialRuleForm';
import {
  CUSTOM_TOAST_DURATION,
  CUSTOM_TOAST_DURATION_NULL,
  DATE_TO_SHOW_WITH_FORMAT,
  TOAST_SETTINGS,
} from '../../constants';
import {
  addFinancialRuleByState,
  deleteFinancialRuleByState,
  getFinancialRulesByState,
  updateFinancialRuleByState,
} from '../../redux/action/client.action';
import { filterErrorData } from '../../utils/helper';

const FinancialRuleList = function ({
  isOpen,
  onClose,
  clientId,
  clientWeekDay,
  selectedState,
  setRulesChanged,
}) {
  const toast = useToast();
  const { user_details } = useSelector((state) => state.authentication);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [openFinancialRuleModal, setOpenFinancialRuleModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteRuleId, setDeleteRuleId] = useState(false);
  const [financialRules, setFinancialRules] = useState([]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';

    return DATE_TO_SHOW_WITH_FORMAT(moment(dateStr).toDate(), user_details.date_format);
  };

  const fetchFinancialRules = async () => {
    try {
      setLoading(true);
      const result = await getFinancialRulesByState(clientId, selectedState?.id);

      if (result.ok && result.data.ok) {
        setFinancialRules(result.data.rules);
      }
    } catch (error) {
      toast({
        title: 'Error fetching rules',
        status: 'error',
        duration: 3000,
      });
    }
    setLoading(false);
  };
  useEffect(() => {
    if (clientId && selectedState?.id) {
      fetchFinancialRules();
    }
  }, [clientId, selectedState?.id]);

  const handleDelete = async (ruleId) => {
    setDeleteRuleId(ruleId);
    setIsDeleteModalOpen(true);
  };
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };
  const confirmDelete = async () => {
    try {
      if (deleteRuleId) {
        const result = await deleteFinancialRuleByState(clientId, selectedState?.id, deleteRuleId);
        if (result.ok && result.data.ok) {
          setRulesChanged(true);
          fetchFinancialRules();
          toast({
            ...TOAST_SETTINGS.SETTINGS,
            duration: CUSTOM_TOAST_DURATION.duration,
            status: TOAST_SETTINGS.SUCCESS,
            title: result.message,
          });
        } else {
          toast({
            ...TOAST_SETTINGS.SETTINGS,
            duration: CUSTOM_TOAST_DURATION_NULL.duration,
            status: TOAST_SETTINGS.ERROR,
            title: filterErrorData(result.error),
          });
        }
      }
    } catch (error) {
      toast({
        ...TOAST_SETTINGS.SETTINGS,
        duration: CUSTOM_TOAST_DURATION.duration,
        status: TOAST_SETTINGS.ERROR,
        title: 'Failed to delete rule',
      });
    }
    closeDeleteModal();
  };

  const handleEdit = (index) => {
    setSelectedRule(financialRules[index]);

    setIsEditMode(true);
    setOpenFinancialRuleModal(true);
  };

  const handleAdd = () => {
    if (financialRules.length) {
      setSelectedRule(financialRules[financialRules.length - 1]);
    }
    setIsEditMode(false);
    setOpenFinancialRuleModal(true);
  };

  const handleSave = async (formData) => {
    try {
      const payload = {
        start_date: formData.weekStartDate.format('YYYY-MM-DD'),
        end_date: formData.weekEndDate.format('YYYY-MM-DD'),
        total_weeks: formData.selectedWeeks,
        fica: formData.fica,
        futa: formData.futa,
        suta: formData.suta,
        disability: formData.disability,
        other: formData.other,
        benefits: formData.benefits,
        wc: formData.wc,
        personnel: formData.personnel,
        opex_overspend: formData.opex_overspend,
        financing_cost_overspend: formData.financing_cost_overspend,
      };
      let result;

      if (isEditMode) {
        result = await updateFinancialRuleByState(
          clientId,
          selectedState?.id,
          selectedRule.id,
          payload
        );
      } else {
        result = await addFinancialRuleByState(clientId, selectedState?.id, payload);
      }

      if (result.ok && result.data.ok) {
        setRulesChanged(true);
        fetchFinancialRules();
        toast({
          ...TOAST_SETTINGS.SETTINGS,
          duration: CUSTOM_TOAST_DURATION.duration,
          status: TOAST_SETTINGS.SUCCESS,
          title: result.message,
        });
      } else {
        toast({
          ...TOAST_SETTINGS.SETTINGS,
          duration: CUSTOM_TOAST_DURATION_NULL.duration,
          status: TOAST_SETTINGS.ERROR,
          title: filterErrorData(result.error),
        });
      }
      setIsEditMode(false);
      setSelectedRule(null);
      setOpenFinancialRuleModal(false);
    } catch (error) {
      toast({
        ...TOAST_SETTINGS.SETTINGS,
        duration: CUSTOM_TOAST_DURATION.duration,
        status: TOAST_SETTINGS.ERROR,
        title: 'Something went wrong try again later',
      });
    }
  };
  return (
    <>
      <FinancialRuleForm
        isOpen={openFinancialRuleModal}
        onClose={() => {
          setSelectedRule(null);
          setOpenFinancialRuleModal(false);
        }}
        isEdit={isEditMode}
        defaultDate={selectedRule?.end_date}
        onSave={handleSave}
        clientWeekDay={clientWeekDay}
        selectedData={selectedRule}
        selectedIndex={financialRules.findIndex((rule) => rule.id === selectedRule?.id)}
        rulesExists={!!financialRules.length}
        selectedState={selectedState}
      />
      <ModalComponent
        specifySize="6xl"
        isOpen={isOpen}
        onClose={onClose}
        modalTitle={
          selectedState
            ? `Manage Financial Rules - ${selectedState.name}`
            : 'Manage Financial Rules'
        }
        modalContent={
          <VStack spacing={4} align="stretch">
            <Flex mb="10px" justify="space-between" align="center">
              <Flex cursor="pointer" alignItems="center" onClick={handleAdd}>
                <Image mr="10px" src={AddIcon} height={6} />
                <Label label="Add Rule" color="main.primary" />
              </Flex>
            </Flex>
            {loading ? (
              <Flex justify="center" align="center" height="200px">
                <Spinner size="md" />
              </Flex>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th px={2} py={1} fontWeight="bold" color="blue.600">
                        Financial Year
                      </Th>
                      <Th px={2} py={1} fontWeight="bold" color="blue.600">
                        Start Date
                      </Th>
                      <Th px={2} py={1} fontWeight="bold" color="blue.600">
                        End Date
                      </Th>
                      <Th px={2} py={1} fontWeight="bold" color="blue.600">
                        FICA
                      </Th>
                      <Th px={2} py={1} fontWeight="bold" color="blue.600">
                        FUTA
                      </Th>
                      <Th px={2} py={1} fontWeight="bold" color="blue.600">
                        SUTA
                      </Th>
                      <Th px={2} py={1} fontWeight="bold" color="blue.600">
                        Disability
                      </Th>
                      <Th px={2} py={1} fontWeight="bold" color="blue.600">
                        Other
                      </Th>
                      <Th px={2} py={1} fontWeight="bold" color="blue.600">
                        Benefits
                      </Th>
                      <Th px={2} py={1} fontWeight="bold" color="blue.600">
                        WC
                      </Th>
                      <Th px={2} py={1} fontWeight="bold" color="blue.600">
                        Personnel
                      </Th>
                      <Th px={2} py={1} fontWeight="bold" color="blue.600">
                        OpEx Overspend
                      </Th>
                      <Th px={2} py={1} fontWeight="bold" color="blue.600">
                        Financing Cost Overspend
                      </Th>
                      <Th px={2} py={1} fontWeight="bold" color="blue.600">
                        Actions
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {financialRules.length === 0 ? (
                      <Tr>
                        <Td colSpan="14">
                          <Text align="center">
                            No Financial Rules Available. Click the + icon to add a new rule.
                          </Text>
                        </Td>
                      </Tr>
                    ) : (
                      financialRules.map((rule, idx) => (
                        <Tr key={rule.id}>
                          <Td px={2} py={1}>
                            {rule.finacial_year_start} - {rule.finacial_year_end}
                          </Td>
                          <Td px={2} py={1}>
                            {formatDate(rule.start_date)}
                          </Td>
                          <Td px={2} py={1}>
                            {formatDate(rule.end_date)}
                          </Td>
                          <Td px={2} py={1}>
                            {rule.fica}
                          </Td>
                          <Td px={2} py={1}>
                            {rule.futa}
                          </Td>
                          <Td px={2} py={1}>
                            {rule.suta}
                          </Td>
                          <Td px={2} py={1}>
                            {rule.disability}
                          </Td>
                          <Td px={2} py={1}>
                            {rule.other}
                          </Td>
                          <Td px={2} py={1}>
                            {rule.benefits}
                          </Td>
                          <Td px={2} py={1}>
                            {rule.wc}
                          </Td>
                          <Td px={2} py={1}>
                            {rule.personnel}
                          </Td>
                          <Td px={2} py={1}>
                            {rule.opex_overspend}
                          </Td>
                          <Td px={2} py={1}>
                            {rule.financing_cost_overspend}
                          </Td>
                          <Td px={2} py={1}>
                            {/* Add loading state */}
                            <HStack spacing={2}>
                              <IconButton
                                size="sm"
                                icon={<Image src={DeleteBlueIcon} height={4} />}
                                onClick={() => handleDelete(rule.id)}
                                variant="ghost"
                              />
                              <Button size="sm" onClick={() => handleEdit(idx)}>
                                Edit
                              </Button>
                            </HStack>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            )}
          </VStack>
        }
      />
      <AlertModalOverlay
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        title="Delete Rule"
        body={
          <>
            <Text fontSize="md" fontWeight="semibold">
              Are you sure you want to delete this rule?
            </Text>
            <Text fontSize="sm" color="red" mt={5}>
              Note: If you delete an initial or intermediate rule, the following rule will also be
              deleted.
            </Text>
          </>
        }
        negative={
          <Button
            onClick={closeDeleteModal}
            bg="main.secondary"
            mr={3}
            _hover={{ bg: 'main.secondary' }}
            color="main.black"
          >
            Cancel
          </Button>
        }
        positive={
          <Button onClick={confirmDelete} colorScheme="blue" ml={3}>
            Delete
          </Button>
        }
      />
    </>
  );
};

export default FinancialRuleList;
